import React, { type ReactElement, useState, useCallback, type KeyboardEvent } from 'react';
import { styled } from '@compiled/react';
import keycode from 'keycode';
import AKBadge, { type BadgeProps } from '@atlaskit/badge';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

export type Props = {
	appearance?: BadgeProps['appearance'];
	style?: BadgeProps['style'];
	children: number | string;
	max: number;
	screenReaderMessage: string;
	tooltipContent: ReactElement | string;
	tooltipDelay?: number; // Required for visual regression testing of the tooltip
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default ({
	appearance,
	style,
	children,
	max,
	screenReaderMessage,
	tooltipContent,
	tooltipDelay,
}: Props) => {
	const [active, setActive] = useState<boolean>(false);
	const onFocus = useCallback(() => {
		setActive(true);
	}, []);
	const onBlur = useCallback(() => {
		setActive(false);
	}, []);
	const onKeyDown = useCallback((event: KeyboardEvent<HTMLSpanElement>) => {
		if (event.keyCode === keycode('enter') || event.keyCode === keycode('space')) {
			event.preventDefault();
			setActive((prev) => !prev);
		}
	}, []);

	const tooltipMessage = active ? tooltipContent : '';

	return (
		<Tooltip content={tooltipMessage} delay={tooltipDelay}>
			<EstimationBadgeWrapper
				// @ts-expect-error tabIndex should be a number
				tabIndex="0"
				onFocus={onFocus}
				onBlur={onBlur}
				onMouseLeave={onBlur}
				onMouseEnter={onFocus}
				onKeyDown={onKeyDown}
				aria-label={screenReaderMessage}
				data-testid="software-backlog.card-list.estimations.badge.estimation-badge-wrapper"
			>
				<AKBadge
					appearance={appearance}
					// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={style}
					max={max}
				>
					{children}
				</AKBadge>
			</EstimationBadgeWrapper>
		</Tooltip>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EstimationBadgeWrapper = styled.span({
	marginLeft: token('space.050', '4px'),
	fontWeight: 'bold',
});
